/**
 *  KIR-EXPORT OLVASÁSA
 *
 *  A KIR táblázatai nem „egy fejléc + adatsorok" felépítésűek:
 *
 *    0. sor  fejléc
 *    1. sor  MINTASOR (kitalált ember: „Minta Géza", 70123456789)
 *    2. sor  fejléc ÚJRA (néhol más címkékkel)
 *    3. sor– a valódi adat
 *
 *  A szokásos beolvasás az ELSŐ sort veszi fejlécnek, így a mintasor és a második
 *  fejlécsor is adatsorként jön be – az egyik kitalált embert importálna, a másik a
 *  saját oszlopneveit. Ez a modul megkeresi a fejlécsorokat, és az UTOLSÓ után kezdi
 *  az adatot: ezzel a mintasor és az ismételt fejléc is magától kiesik.
 *
 *  Az oszlopnevek minden fejlécsorból összeállnak (oszloponként az első nem üres
 *  címke), mert a KIR néha épp az ismételt fejlécben hagy üresen egy nevet.
 */

export interface KirOlvasas {
  /** Az oszlopnevek a megtalált fejléc(ek)ből. */
  fejlec: string[];
  /** Az adatsorok objektumként, oszlopnév → érték. */
  sorok: any[];
  /** Hány fejlécsort találtunk (1 fölött volt ismételt fejléc). */
  fejlecDb: number;
  /** Hány sort dobtunk el a bevezető fejlécek között (mintasor). */
  mintaDb: number;
  /** Hány ISMÉTELT fejlécsor esett ki az adat közül/végéről. */
  ismeteltFejlec: number;
  /** Hányadik sorban volt az utolsó fejléc (0-alapú) – a hibaüzenethez. */
  adatKezd: number;
}

function szoveg(v: any): string {
  return String(v ?? '').replace(/\s+/g, ' ').trim();
}

/** Ékezet- és kisbetű-független oszlopnév-kulcs (a KIR helyenként másképp ír). */
function oszlopKulcs(v: any): string {
  return szoveg(v).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 *  @param matrix   a munkalap nyers sorai (XLSX sheet_to_json header:1)
 *  @param kulcsok  olyan oszlopnevek, amelyek EGYÜTT azonosítják a fejlécsort
 */
export function kirBeolvas(matrix: any[][], kulcsok: string[]): KirOlvasas {
  const keresett = kulcsok.map(oszlopKulcs);
  const fejlecSorok: number[] = [];
  for (let i = 0; i < matrix.length; i++) {
    const cellak = (matrix[i] || []).map(oszlopKulcs);
    if (keresett.every((k) => cellak.includes(k))) fejlecSorok.push(i);
  }
  if (!fejlecSorok.length) {
    return { fejlec: [], sorok: [], fejlecDb: 0, mintaDb: 0, ismeteltFejlec: 0, adatKezd: -1 };
  }

  /*  A BEVEZETŐ fejléc-blokk.
   *
   *  A KIR a táblázat VÉGÉRE is odaírja a fejlécet még egyszer. Ha egyszerűen az
   *  utolsó fejléc után kezdenénk az adatot, az egész fájl eltűnne (a próbán pontosan
   *  ez történt: 80 sorból 0 adatsor). A bevezető fejlécek EGYMÁS MELLETT vannak,
   *  legfeljebb a mintasor van közöttük – a záró fejléc messze esik tőlük.
   */
  const bevezeto = [fejlecSorok[0]];
  for (let k = 1; k < fejlecSorok.length; k++) {
    if (fejlecSorok[k] - fejlecSorok[k - 1] > 3) break;
    bevezeto.push(fejlecSorok[k]);
  }

  // Oszlopnevek: oszloponként az első nem üres címke bármelyik fejlécsorból.
  const szelesseg = Math.max(...fejlecSorok.map((i) => (matrix[i] || []).length));
  const fejlec: string[] = [];
  for (let o = 0; o < szelesseg; o++) {
    let nev = '';
    for (const i of fejlecSorok) {
      const c = szoveg((matrix[i] || [])[o]);
      if (c) { nev = c; break; }
    }
    // A névtelen oszlopnak is kell azonosító, különben egymásra írnák egymást.
    fejlec.push(nev || `oszlop_${o + 1}`);
  }

  const utolsoBevezeto = bevezeto[bevezeto.length - 1];
  const fejlecHalmaz = new Set(fejlecSorok);
  const sorok: any[] = [];
  let ismeteltFejlec = 0;
  for (let i = utolsoBevezeto + 1; i < matrix.length; i++) {
    const nyers = matrix[i] || [];
    if (!nyers.some((c) => szoveg(c) !== '')) continue;   // üres sor
    // A táblázat közepén/végén megismételt fejléc nem adat.
    if (fejlecHalmaz.has(i)) { ismeteltFejlec++; continue; }
    const sor: any = {};
    fejlec.forEach((nev, o) => { sor[nev] = nyers[o] ?? null; });
    sorok.push(sor);
  }

  // A bevezető fejlécek KÖZÖTTI sorok a KIR mintasorai – ezeket sosem importáljuk.
  let mintaDb = 0;
  for (let i = bevezeto[0] + 1; i < utolsoBevezeto; i++) {
    if (fejlecHalmaz.has(i)) continue;
    if ((matrix[i] || []).some((c) => szoveg(c) !== '')) mintaDb++;
  }

  return {
    fejlec, sorok, fejlecDb: fejlecSorok.length, mintaDb, ismeteltFejlec,
    adatKezd: utolsoBevezeto + 1,
  };
}

/**
 *  KIR-dátum → YYYY-MM-DD.
 *
 *  A KIR „2012. 01. 10." alakot ír (szóközökkel, ponttal a végén). Ezt kizárólag
 *  FELSOROLT alakokkal olvassuk: a szabad kezű elemzés a böngésző saját dátum-
 *  értelmezésére esne vissza, ami böngészőnként MÁS – ugyanaz a fájl más gépen más
 *  (vagy érvénytelen) dátumot adna.
 */
const KIR_DATUM_ALAKOK = [
  'YYYY. MM. DD.', 'YYYY. M. D.', 'YYYY.MM.DD.', 'YYYY.MM.DD', 'YYYY. MM. DD',
  'YYYY-MM-DD', 'YYYY/MM/DD', 'M/D/YY', 'M/D/YYYY', 'DD.MM.YYYY',
];

export function kirDatum(ertek: any, moment: any): string | null {
  if (ertek === null || ertek === undefined || ertek === '') return null;
  if (ertek instanceof Date) {
    const m = moment(ertek);
    return m.isValid() ? m.format('YYYY-MM-DD') : null;
  }
  const t = szoveg(ertek);
  if (!t) return null;
  const m = moment(t, KIR_DATUM_ALAKOK, true);
  if (m.isValid()) return m.format('YYYY-MM-DD');
  // Utolsó esély: a puszta számokból összerakott ÉÉÉÉHHNN.
  const szamok = t.match(/(\d{4})\D+(\d{1,2})\D+(\d{1,2})/);
  if (szamok) {
    const m2 = moment(`${szamok[1]}-${szamok[2].padStart(2, '0')}-${szamok[3].padStart(2, '0')}`, 'YYYY-MM-DD', true);
    if (m2.isValid()) return m2.format('YYYY-MM-DD');
  }
  return null;
}

/* ─────────────────────────────────────────────────────────────────────────
 *  TELJES NÉV SZÉTBONTÁSA  (előtag / vezetéknév / keresztnév)
 *
 *  A KRÉTA gondviselő-exportja EGYETLEN mezőben adja a nevet („Dr. Apáthyné
 *  Dr. Csuha Réka"), a rendszer viszont három mezőben tárolja. A szétbontás
 *  magyar névre nem oldható meg hibátlanul - „Nagy Anna Mária" lehet két
 *  keresztnév vagy kéttagú vezetéknév -, ezért a szabály KIMONDOTT, és az
 *  eredmény az importálás ELŐTT látszik az előnézetben:
 *
 *    1. a bevezető megszólítások (Dr., Prof., id., ifj., özv.) az előtagba,
 *    2. a vezetéknév az első szó - kivéve, ha „-né" végű vagy megszólítás
 *       követi, mert az házassági névforma („Apáthyné Dr. Csuha Réka"),
 *    3. ami marad, az a keresztnév.
 * ───────────────────────────────────────────────────────────────────────── */

const NEV_ELOTAGOK = new Set([
  'dr', 'dr.', 'drs', 'prof', 'prof.', 'id', 'id.', 'ifj', 'ifj.', 'özv', 'özv.',
  'özvegy', 'ph.d', 'phd', 'dr.h.c', 'med', 'med.',
]);

function elotagE(szo: string): boolean {
  return NEV_ELOTAGOK.has(szoveg(szo).toLowerCase().replace(/,$/, ''));
}

/** Házassági névforma jele: „-né" végződés vagy a névbe ágyazott megszólítás. */
function vezeteknevFolytatodik(szo: string): boolean {
  const sz = szoveg(szo).toLowerCase();
  return elotagE(sz) || /né$/.test(sz);
}

export function nevBont(teljes: any): { elo: string; vez: string; ker: string } {
  const szavak = szoveg(teljes).split(' ').filter(Boolean);
  if (!szavak.length) return { elo: '', vez: '', ker: '' };

  // 1. bevezető megszólítások
  const elo: string[] = [];
  while (szavak.length > 1 && elotagE(szavak[0])) elo.push(szavak.shift());

  if (szavak.length === 1) return { elo: elo.join(' '), vez: szavak[0], ker: '' };

  // 2. vezetéknév: az első szó, és minden további, amit házassági névforma indokol
  const vez = [szavak.shift()];
  while (szavak.length > 1 && vezeteknevFolytatodik(vez[vez.length - 1])) {
    vez.push(szavak.shift());
  }

  // 3. a maradék a keresztnév
  return { elo: elo.join(' '), vez: vez.join(' '), ker: szavak.join(' ') };
}

/**
 *  A KRÉTA összevont cím-mezőinek tisztítása.
 *
 *  Az emelet/ajtó rovat kitöltetlenül is tartalmat kap: „ép.-----, em.-----,
 *  lépcső:-----, ajtó:-----". Ezt változatlanul beemelve minden címbe bekerülne
 *  egy sor kötőjel. A kitöltetlen szakaszokat ezért eldobjuk.
 */
export function kretaCimResz(ertek: any): string {
  const t = szoveg(ertek);
  if (!t) return '';
  if (!t.includes(':') && !/-{3,}/.test(t)) return t;
  return t.split(',')
    .map((r) => r.trim())
    .filter((r) => {
      const ertekResz = r.includes(':') ? r.split(':').slice(1).join(':') : r.replace(/^\S+\.?\s*/, '');
      const tiszta = ertekResz.replace(/[-\s]/g, '');
      return !!tiszta;
    })
    .join(', ');
}
